import { useState, useRef, useEffect } from "react";
import { MapContainer, TileLayer, Polygon, Tooltip, CircleMarker, Polyline, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { C } from "./ui";

// Ícono de "manija" para arrastrar los vértices al editar un potrero
const HANDLE_ICON = L.divIcon({
  className: "",
  html: `<div style="width:15px;height:15px;border-radius:50%;background:#FDFCF8;border:2.5px solid ${C.pasto};box-shadow:0 0 4px rgba(0,0,0,0.55);cursor:move;"></div>`,
  iconSize: [15, 15],
  iconAnchor: [7.5, 7.5],
});

// Ícono "➕" en el medio de cada lado: al clickearlo se inserta un vértice
const ADD_ICON = L.divIcon({
  className: "",
  html: `<div style="width:14px;height:14px;border-radius:50%;background:rgba(253,252,248,0.55);border:2px dashed ${C.pasto};display:flex;align-items:center;justify-content:center;color:${C.pasto};font-size:12px;font-weight:700;line-height:1;cursor:copy;">+</div>`,
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

const UY_CENTER = [-32.8, -56.0];
const UY_ZOOM   = 7;
const UY_MIN_ZOOM = 6;   // no permitir alejarse más que esto (sino el mapa queda en blanco)
// Límites generosos alrededor de Uruguay para que no se pueda salir a zonas sin tiles
const UY_BOUNDS = [[-35.6, -59.2], [-29.5, -52.3]];

const CAMPO_LIBRE_COLOR = "#8FCB6E"; // verde claro para potreros "a campo libre"

const TILES = [
  {
    id:     "satelite",
    l:      "🛰 Satélite",
    url:    "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attr:   "Tiles &copy; Esri — Esri, USDA, USGS, AEX, GeoEye, Getmapping, IGN, GIS User Community",
    maxZoom: 19,
  },
  {
    id:     "topo",
    l:      "🏔 Relieve",
    url:    "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
    attr:   "Map data: &copy; OpenStreetMap contributors, SRTM | Style: &copy; OpenTopoMap (CC-BY-SA)",
    maxZoom: 17,
  },
  {
    id:     "calles",
    l:      "🗺 Calles",
    url:    "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attr:   "&copy; <a href='https://www.openstreetmap.org/copyright'>OpenStreetMap</a>",
    maxZoom: 19,
  },
];

const ESTADO_COLOR = { activo:"#3D6B41", descanso:"#B8943A", disponible:"#4A7FB5", sembrado:"#B86040" };
const ESTADO_LABEL = { activo:"Pastoreando", descanso:"Descanso", disponible:"Disponible", sembrado:"Sembrado" };

// ─── ZOOM AL POTRERO SELECCIONADO ────────────────────────
function MapZoomer({ potreros, selectedId }) {
  const map = useMap();
  useEffect(() => {
    if (!selectedId) return;
    const pot = potreros.find(p => p.id === selectedId);
    if (!pot?.points?.length) return;
    const lats = pot.points.map(p => p[0]);
    const lngs = pot.points.map(p => p[1]);
    const bounds = [[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]];
    map.flyToBounds(bounds, { padding: [60, 60], duration: 0.8 });
  }, [selectedId, map, potreros]);
  return null;
}

// ─── ENCUADRAR TODOS LOS POTREROS (al cargar) ────────────
function FitAllPotreros({ potreros, selectedId }) {
  const map = useMap();
  const done = useRef(false);
  useEffect(() => {
    if (done.current || selectedId) return;
    const pts = potreros.flatMap(p => p.points || []);
    if (!pts.length) return;
    const lats = pts.map(p => p[0]);
    const lngs = pts.map(p => p[1]);
    map.fitBounds(
      [[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]],
      { padding: [50, 50], maxZoom: 15 }
    );
    done.current = true;
  }, [potreros, selectedId, map]);
  return null;
}

// ─── CAPA DE DIBUJO ───────────────────────────────────────
function DrawingLayer({ drawing, currentPtsRef, onPoint, onFinish }) {
  const map = useMap();

  useEffect(() => {
    if (drawing) {
      map.doubleClickZoom.disable();
      map.getContainer().style.cursor = "crosshair";
    } else {
      map.doubleClickZoom.enable();
      map.getContainer().style.cursor = "";
    }
  }, [drawing, map]);

  useMapEvents({
    click(e) {
      if (!drawing) return;
      onPoint([e.latlng.lat, e.latlng.lng]);
    },
    dblclick(e) {
      if (!drawing) return;
      e.originalEvent.preventDefault();
      e.originalEvent.stopPropagation();
      // dblclick dispara: click(punto válido) + click(duplicado) + dblclick
      // Descartamos solo el duplicado (-1), el primer click es intencional
      const clean = currentPtsRef.current.slice(0, -1);
      if (clean.length >= 3) onFinish(clean);
    },
  });

  return null;
}

// ─── COMPONENTE PRINCIPAL (exported) ─────────────────────
export function MapaLeaflet({
  potreros, drawing, currentPts,
  onPoint, onFinish, onSelectPotrero, selectedId,
  editingId, onVertexDrag, onVertexDelete, onVertexInsert, onRemovePoint, onFinishEdit, onCancelEdit,
}) {
  const potEdit = editingId ? potreros.find(p => p.id === editingId) : null;
  const [capaTiles, setCapaTiles] = useState("satelite");
  const currentPtsRef = useRef(currentPts);
  currentPtsRef.current = currentPts;

  const tile      = TILES.find(t => t.id === capaTiles);
  const totalHa   = potreros.reduce((s, p) => s + p.ha, 0);
  const totalAnim = potreros.reduce((s, p) => s + (p.animalesAsignados || []).length, 0);

  const hintText = drawing
    ? currentPts.length < 3
      ? `${currentPts.length} punto${currentPts.length !== 1 ? "s" : ""} · click para agregar · doble-click en un punto lo borra`
      : `${currentPts.length} puntos · cerrá con ✓ del panel · doble-click en un punto lo borra`
    : potreros.length === 0
      ? 'Usá "+ Dibujar potrero" en el panel para comenzar'
      : `${potreros.length} potrero${potreros.length !== 1 ? "s" : ""} · ${totalHa.toFixed(1)} ha · ${totalAnim} animales`;

  return (
    <div style={{ flex: 1, position: "relative", overflow: "hidden", minWidth: 0 }}>

      {/* ── MAPA LEAFLET ── */}
      <MapContainer
        center={UY_CENTER}
        zoom={UY_ZOOM}
        minZoom={UY_MIN_ZOOM}
        maxBounds={UY_BOUNDS}
        maxBoundsViscosity={1.0}
        style={{ width: "100%", height: "100%", zIndex: 0 }}
        zoomControl
      >
        <TileLayer url={tile.url} attribution={tile.attr} minZoom={UY_MIN_ZOOM} maxZoom={tile.maxZoom}/>

        <DrawingLayer
          drawing={drawing}
          currentPtsRef={currentPtsRef}
          onPoint={onPoint}
          onFinish={onFinish}
        />
        <FitAllPotreros potreros={potreros} selectedId={selectedId}/>
        <MapZoomer potreros={potreros} selectedId={selectedId}/>

        {potreros.map(pot => {
          const nAnim = (pot.animalesAsignados || []).length;
          const hasAnim = nAnim > 0;
          const baseColor = pot.tipo === "libre" ? CAMPO_LIBRE_COLOR : pot.color;
          return (
          <Polygon
            key={pot.id}
            positions={pot.points}
            pathOptions={{
              color:       selectedId === pot.id ? "#FDFCF8" : baseColor,
              fillColor:   baseColor,
              // Con animales: más sólido y borde grueso. Sin animales: muy tenue.
              fillOpacity: selectedId === pot.id ? 0.72 : (hasAnim ? 0.6 : 0.12),
              weight:      selectedId === pot.id ? 4 : (hasAnim ? 3.5 : 1.5),
              dashArray:   pot.tipo === "libre" ? "5,5" : (hasAnim ? undefined : "3,4"),
            }}
            eventHandlers={{ click: () => { if (!editingId) onSelectPotrero(pot.id); } }}
          >
            <Tooltip permanent direction="center" opacity={1}>
              <strong style={{ fontSize: 12 }}>
                {pot.nombre}
                {pot.tipo === "libre" ? " 🌾" : ""}
              </strong><br/>
              {hasAnim ? (
                <span style={{ fontSize: 11, fontWeight: 700, color: "#1A5228" }}>
                  🐂 {nAnim} animal{nAnim !== 1 ? "es" : ""}
                </span>
              ) : (
                <span style={{ fontSize: 10, opacity: 0.6, fontStyle: "italic" }}>
                  sin animales
                </span>
              )}
              <br/>
              <span style={{ fontSize: 10, opacity: 0.8 }}>
                {pot.ha.toFixed(1)} ha{pot.tipo === "libre" ? " · campo libre" : ""}
              </span>
            </Tooltip>
          </Polygon>
          );
        })}

        {/* Vértices arrastrables del potrero en edición */}
        {potEdit && potEdit.points.map((pt, idx) => (
          <Marker
            key={`v-${idx}`}
            position={pt}
            draggable
            icon={HANDLE_ICON}
            eventHandlers={{
              drag: (e) => {
                const ll = e.target.getLatLng();
                onVertexDrag(editingId, idx, [ll.lat, ll.lng]);
              },
              dblclick: (e) => {
                L.DomEvent.stop(e);
                if (potEdit.points.length > 3) onVertexDelete(editingId, idx);
              },
            }}
          />
        ))}

        {/* Manijas "+" en el medio de cada lado: insertan un vértice */}
        {potEdit && potEdit.points.map((pt, idx) => {
          const next = potEdit.points[(idx + 1) % potEdit.points.length];
          const mid = [(pt[0] + next[0]) / 2, (pt[1] + next[1]) / 2];
          return (
            <Marker
              key={`mid-${idx}`}
              position={mid}
              icon={ADD_ICON}
              eventHandlers={{
                click: (e) => { L.DomEvent.stop(e); onVertexInsert(editingId, idx + 1, mid); },
              }}
            />
          );
        })}

        {currentPts.length > 1 && (
          <Polyline positions={currentPts}
            pathOptions={{ color: "#FDFCF8", weight: 2, dashArray: "8,5", opacity: 0.9 }}/>
        )}
        {currentPts.length > 2 && (
          <Polygon positions={currentPts}
            pathOptions={{ color: "#FDFCF8", fillOpacity: 0.12, weight: 0 }}/>
        )}
        {currentPts.map((pt, i) => (
          <CircleMarker key={i} center={pt} radius={6}
            bubblingMouseEvents={false}
            pathOptions={{ color: C.tierra, fillColor: C.arena, fillOpacity: 1, weight: 1.5 }}
            eventHandlers={{
              dblclick: (e) => { L.DomEvent.stop(e); onRemovePoint && onRemovePoint(i); },
            }}/>
        ))}
      </MapContainer>

      {/* ── SELECTOR DE CAPA (top-right) ── */}
      <div style={{
        position: "absolute", top: 10, right: 10, zIndex: 1000,
        display: "flex", gap: 3, background: "rgba(20,16,10,0.80)",
        borderRadius: 9, padding: 4, backdropFilter: "blur(4px)",
      }}>
        {TILES.map(t => (
          <button key={t.id} onClick={() => setCapaTiles(t.id)} style={{
            padding: "5px 11px", fontSize: 11, fontFamily: "monospace",
            border: "none", cursor: "pointer", borderRadius: 6,
            background: capaTiles === t.id ? "rgba(255,255,255,0.22)" : "transparent",
            color: C.arena, fontWeight: capaTiles === t.id ? 700 : 400,
            opacity: capaTiles === t.id ? 1 : 0.6, transition: "all 0.15s",
          }}>{t.l}</button>
        ))}
      </div>

      {/* ── LEYENDA DE ESTADOS (bottom-left, transparente) ── */}
      <div style={{
        position: "absolute", bottom: 14, left: 14, zIndex: 1000,
        background: "rgba(20,16,10,0.72)", borderRadius: 9, padding: "9px 12px",
        backdropFilter: "blur(4px)",
      }}>
        {Object.entries(ESTADO_COLOR).map(([k, color]) => (
          <div key={k} style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 5 }}>
            <div style={{ width: 9, height: 9, borderRadius: 2, background: color, flexShrink: 0 }}/>
            <span style={{ fontSize: 11, color: "rgba(255,255,255,0.85)", fontFamily: "monospace" }}>
              {ESTADO_LABEL[k]}
            </span>
          </div>
        ))}
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.3)", marginTop: 6, paddingTop: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 5 }}>
            <div style={{ width: 11, height: 11, borderRadius: 2, background: "rgba(255,255,255,0.75)",
              flexShrink: 0, border: "2px solid rgba(255,255,255,0.95)" }}/>
            <span style={{ fontSize: 11, color: "rgba(255,255,255,0.85)", fontFamily: "monospace" }}>
              🐂 Con animales
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 5 }}>
            <div style={{ width: 11, height: 11, borderRadius: 2, background: "rgba(255,255,255,0.10)",
              flexShrink: 0, border: "1px dashed rgba(255,255,255,0.45)" }}/>
            <span style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", fontFamily: "monospace" }}>
              Sin animales
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <div style={{ width: 9, height: 9, borderRadius: 2, background: CAMPO_LIBRE_COLOR,
              flexShrink: 0, border: "1px dashed rgba(255,255,255,0.7)" }}/>
            <span style={{ fontSize: 11, color: "rgba(255,255,255,0.85)", fontFamily: "monospace" }}>
              Campo libre
            </span>
          </div>
        </div>
      </div>

      {/* ── BANNER DE EDICIÓN DE FORMA (top-center) ── */}
      {potEdit && (
        <div style={{
          position: "absolute", top: 12, left: "50%", transform: "translateX(-50%)",
          zIndex: 1100, display: "flex", alignItems: "center", gap: 12,
          background: C.tierra, color: C.arena, borderRadius: 22, padding: "8px 10px 8px 16px",
          boxShadow: "0 3px 12px rgba(0,0,0,0.35)", fontSize: 13, whiteSpace: "nowrap",
        }}>
          <span>✏️ <strong>{potEdit.nombre}</strong> · arrastrá esquinas · ➕ del lado agrega · doble-click borra</span>
          <button onClick={onCancelEdit} style={{
            background: "transparent", color: C.arena, border: `1px solid rgba(255,255,255,0.3)`,
            borderRadius: 16, padding: "6px 12px", fontSize: 13, cursor: "pointer",
          }}>Cancelar</button>
          <button onClick={onFinishEdit} style={{
            background: C.pasto, color: "#fff", border: "none", borderRadius: 16,
            padding: "6px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer",
          }}>✓ Listo</button>
        </div>
      )}

      {/* ── HINT BAR (bottom-center) ── */}
      <div style={{
        position: "absolute", bottom: 14, left: "50%", transform: "translateX(-50%)",
        background: "rgba(20,16,10,0.80)", color: C.arena, fontSize: 12,
        padding: "7px 18px", borderRadius: 20, pointerEvents: "none",
        whiteSpace: "nowrap", fontFamily: "monospace", zIndex: 1000,
      }}>
        {hintText}
      </div>
    </div>
  );
}
